import { motion } from 'framer-motion';
import React from 'react';
import { useInView } from 'react-intersection-observer';

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  description?: string;
  centered?: boolean;
  accent?: boolean;
  className?: string;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  description,
  centered = true,
  accent = false,
  className = ''
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      ref={ref}
      variants={headerVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className={`${centered ? 'text-center' : ''} ${className}`}
    >
      {subtitle && (
        <motion.div variants={itemVariants}>
          <span className={`
            inline-block text-sm font-semibold tracking-wider uppercase mb-3
            ${accent ? 'text-brand-primary' : 'text-gray-600'}
          `}>
            {subtitle}
          </span>
        </motion.div>
      )}
      
      <motion.h2 
        variants={itemVariants}
        className={`
          text-3xl md:text-4xl lg:text-5xl font-bold mb-6 font-heading leading-relaxed
          ${accent ? 'text-gradient' : 'text-government-navy'}
        `}
      >
        {title}
      </motion.h2>
      
      {description && (
        <motion.p 
          variants={itemVariants}
          className={`
            text-lg text-gray-600 leading-relaxed
            ${centered ? 'max-w-3xl mx-auto' : 'max-w-2xl'}
          `}
        >
          {description}
        </motion.p>
      )}
    </motion.div>
  );
};